import * as Sentry from '@sentry/angular';
import { Inject, Injectable, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { environment } from '../../../environments/environment';

type AmpMod = typeof import('@amplitude/analytics-browser');
type AmpReplayMod = typeof import('@amplitude/plugin-session-replay-browser');

@Injectable({ providedIn: 'root' })
export class IntegrationsService {
  private browser = false;
  private gaReady = false;
  private hotjarReady = false;
  private amplitudeReady = false;
  private amp?: AmpMod;
  private pendingAmpEvents: Array<{
    event: string;
    props?: Record<string, any>;
  }> = [];

  constructor(@Inject(PLATFORM_ID) platformId: Object) {
    this.browser = isPlatformBrowser(platformId);
  }

  async initAll() {
    if (!this.browser || !environment.production) return;

    await this.waitForLoad();

    await Promise.all([
      this.initHotjar(),
      this.initGA(),
      this.initAmplitude('88b13fdb3e6ef0623400a5b891188586'),
    ]);
  }

  private async initAmplitude(apiKey: string) {
    if (this.amplitudeReady) return;
    const [amp, replay] = await Promise.all([
      import('@amplitude/analytics-browser') as Promise<AmpMod>,
      import(
        '@amplitude/plugin-session-replay-browser'
      ) as Promise<AmpReplayMod>,
    ]);

    this.amp = amp;

    amp.add(replay.sessionReplayPlugin({ sampleRate: 1 }));
    amp.init(apiKey, undefined, {
      autocapture: { elementInteractions: true },
      defaultTracking: true,
    });

    this.amplitudeReady = true;

    // On init go through que as there might be some events
    for (const { event, props } of this.pendingAmpEvents) {
      amp.track(event, props);
    }
    this.pendingAmpEvents = [];
  }

  private async initGA() {
    if (this.gaReady) return;
    (window as any).dataLayer = (window as any).dataLayer || [];
    const gtag = function () {
      (window as any).dataLayer.push(arguments);
    };
    (window as any).gtag = gtag;

    await this.appendScript(
      'https://www.googletagmanager.com/gtag/js?id=G-43GG48V9RH',
      { async: true }
    );
    // @ts-ignore
    gtag('js', new Date());
    // @ts-ignore
    gtag('config', 'G-43GG48V9RH');
    this.gaReady = true;
  }

  private async initHotjar() {
    if (this.hotjarReady) return;

    try {
      const w = window as any;

      w.hj =
        w.hj ||
        function () {
          (w.hj.q = w.hj.q || []).push(arguments);
        };
      w._hjSettings = { hjid: 3729323, hjsv: 6 };

      const src = `https://static.hotjar.com/c/hotjar-${w._hjSettings.hjid}.js?sv=${w._hjSettings.hjsv}`;
      await this.appendScript(src, {
        async: true,
        crossOrigin: 'anonymous' as any,
      });

      this.hotjarReady = true;
    } catch (e) {
      console.debug('Hotjar failed to load (likely blocked):', e);
    }
  }

  private appendScript(
    src: string,
    attrs: Partial<HTMLScriptElement> = {}
  ): Promise<void> {
    return new Promise((resolve, reject) => {
      const s = document.createElement('script');
      s.src = src;
      Object.assign(s, attrs);
      s.onload = () => resolve();
      s.onerror = reject;
      document.head.appendChild(s);
    });
  }

  private waitForLoad(): Promise<void> {
    if (document.readyState === 'complete') return Promise.resolve();
    return new Promise((res) =>
      window.addEventListener('load', () => res(), { once: true })
    );
  }

  track(
    event: string,
    params?: Record<string, any>,
    opts: { ga?: boolean; amplitude?: boolean } = { ga: true, amplitude: false }
  ) {
    if (opts.ga) this.trackGA(event, params);
    if (opts.amplitude) this.trackAmplitude(event, params);
  }

  trackGA(event: string, params?: Record<string, any>) {
    if (!(window as any).gtag) return;
    (window as any).gtag('event', event, params);
  }

  trackAmplitude(event: string, props?: Record<string, any>) {
    if (!this.amplitudeReady || !this.amp) {
      // Que events to not lose them before init
      this.pendingAmpEvents.push({ event, props });
      return;
    }
    this.amp.track(event, props);
  }
}

export function handleIntegrations(): void {
  initSentry();
}

function initSentry(): void {
  Sentry.init({
    dsn: 'https://60236da900aa8e3fd1eba789807da88e@o4505992663793664.ingest.sentry.io/4505993237692416',
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration(),
      Sentry.captureConsoleIntegration({ levels: ['error'] }),
    ],
    // Set 'tracePropagationTargets' to control for which URLs distributed tracing should be enabled
    tracePropagationTargets: ['localhost', 'https://interactive-map-ai.com/'],
    // Performance Monitoring
    tracesSampleRate: 1.0, // Capture 100% of the transactions, reduce in production!
    // Session Replay
    replaysSessionSampleRate: 0.1, // This sets the sample rate at 10%. You may want to change it to 100% while in development and then sample at a lower rate in production.
    replaysOnErrorSampleRate: 1.0, // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.
  });
}
